import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SlidersHorizontal, Loader2, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

export default function Rubrics() {
  const [rubrics, setRubrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get("/rubrics").then((r) => setRubrics(r.data)).finally(() => setLoading(false));
  }, []);

  const update = (key, patch) => setRubrics((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const updateItem = (key, idx, label) =>
    update(key, { checklist: rubrics.find((r) => r.key === key).checklist.map((c, i) => (i === idx ? { ...c, label } : c)) });
  const addItem = (key) => {
    const r = rubrics.find((x) => x.key === key);
    update(key, { checklist: [...r.checklist, { id: `c_${Date.now()}`, label: "New criterion" }] });
  };
  const removeItem = (key, idx) => {
    const r = rubrics.find((x) => x.key === key);
    update(key, { checklist: r.checklist.filter((_, i) => i !== idx) });
  };

  const save = async (r) => {
    setSaving(r.key);
    try {
      await api.put(`/rubrics/${r.key}`, { threshold: r.threshold, sla_days: r.sla_days, checklist: r.checklist });
      toast.success(`${r.name} exit criteria saved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(null); }
  };

  return (
    <div className="p-6 max-w-4xl">
      <header className="mb-6">
        <h1 className="font-heading text-4xl font-black tracking-tighter flex items-center gap-3">
          <SlidersHorizontal className="w-8 h-8 text-primary" /> Exit-Criteria Templates
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Configure the enforced checklist, thresholds and SLA per stage — no developer needed.</p>
      </header>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="space-y-4">
          {rubrics.map((r) => (
            <div key={r.key} data-testid={`rubric-${r.key}`} className="border border-border bg-card rounded-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-bold tracking-tight">{r.name}</h3>
                <Button data-testid={`save-rubric-${r.key}`} size="sm" onClick={() => save(r)} disabled={saving === r.key} className="rounded-sm h-8">
                  {saving === r.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}<span className="ml-1.5">Save</span>
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <label className="text-xs">
                  <span className="text-muted-foreground uppercase tracking-wider">Pass Threshold</span>
                  <Input type="number" data-testid={`threshold-${r.key}`} value={r.threshold} onChange={(e) => update(r.key, { threshold: parseInt(e.target.value) || 0 })} className="mt-1 rounded-sm bg-background h-8" />
                </label>
                <label className="text-xs">
                  <span className="text-muted-foreground uppercase tracking-wider">SLA (days)</span>
                  <Input type="number" data-testid={`sla-${r.key}`} value={r.sla_days} onChange={(e) => update(r.key, { sla_days: parseInt(e.target.value) || 0 })} className="mt-1 rounded-sm bg-background h-8" />
                </label>
              </div>

              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Checklist items</p>
              <div className="space-y-2">
                {r.checklist.map((c, i) => (
                  <div key={c.id} className="flex items-center gap-2">
                    <Input value={c.label} onChange={(e) => updateItem(r.key, i, e.target.value)} className="rounded-sm bg-background h-8 text-sm" data-testid={`item-${r.key}-${i}`} />
                    <button onClick={() => removeItem(r.key, i)} className="text-muted-foreground hover:text-danger transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={() => addItem(r.key)} className="rounded-sm mt-2 h-7 border-border" data-testid={`add-item-${r.key}`}>
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add criterion
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
